import {
  createFileRoute,
  Link,
  Outlet,
  useMatches,
} from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Trash2, Lock, FlaskConical } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLeagueStrict } from "@/lib/league-context";
import {
  listMyScenarios,
  deleteScenario,
  SCENARIO_CAP_PER_LEAGUE,
} from "@/lib/scenarios.functions";
import { NewScenarioButton } from "@/components/NewScenarioButton";
import { toast } from "sonner";


export const Route = createFileRoute("/_app/$league/scenarios")({
  component: ScenariosPage,
  head: () => ({
    meta: [
      { title: "Scenarios — Bracket Goblin" },
      {
        name: "description",
        content:
          "Run 'what if' scenarios on the rest of the tournament without changing your real picks.",
      },
    ],
  }),
});

function ScenariosPage() {
  const matches = useMatches();
  const hasChild = matches.some(
    (m) => m.routeId === "/_app/$league/scenarios/$scenarioId",
  );
  if (hasChild) return <Outlet />;
  return <ScenariosIndex />;
}

function ScenariosIndex() {
  const league = useLeagueStrict();
  const qc = useQueryClient();
  const list = useServerFn(listMyScenarios);
  const remove = useServerFn(deleteScenario);

  // Scenarios unlock at knockout lock only. Before then the bracket isn't
  // settled and any "what if" would just be group-stage shuffling — not the
  // point of this feature.
  const bracketLockPassed =
    !!league.bracketLockAt && new Date(league.bracketLockAt) <= new Date();
  const preLock = !bracketLockPassed;

  const { data, isLoading } = useQuery({
    queryKey: ["scenarios", league.id],
    queryFn: () => list({ data: { leagueId: league.id } }),
    enabled: !preLock,
  });

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const deleteMut = useMutation({
    mutationFn: (id: string) => remove({ data: { scenarioId: id } }),
    onSuccess: () => {
      setDeleteId(null);
      void qc.invalidateQueries({ queryKey: ["scenarios", league.id] });
      toast.success("Scenario deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });


  if (preLock) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <Lock className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-xl font-semibold mb-2">Scenarios unlock at knockout lock</h1>
          <p className="text-sm text-muted-foreground">
            Once the knockout bracket is locked in, you'll be able to run
            "what if" scenarios — override match results, project bonus
            outcomes, see how the leaderboard would reshuffle. Without
            touching your real picks.
          </p>
        </div>
      </div>
    );
  }

  const slotsUsed = data?.slotsUsed ?? 0;
  const slotsLeft = SCENARIO_CAP_PER_LEAGUE - slotsUsed;
  const atCap = slotsLeft <= 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <FlaskConical className="w-4 h-4" />
            <span>Sandbox · doesn't affect your real picks</span>
          </div>
          <h1 className="text-2xl font-bold">Scenarios</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Run a "what if" — change your own picks, change the actual results,
            see how the leaderboard would reshuffle.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs text-muted-foreground">Slots</div>
          <div className="text-sm font-semibold">
            {slotsUsed} / {SCENARIO_CAP_PER_LEAGUE}
          </div>
        </div>
      </header>

      <NewScenarioButton
        leagueId={league.id}
        leagueSlug={league.slug}
        disabled={atCap}
        className="w-full sm:w-auto"
      />

      {atCap && (
        <p className="text-xs text-muted-foreground -mt-3">
          You've hit the {SCENARIO_CAP_PER_LEAGUE}-scenario cap. Delete one to
          make room.
        </p>
      )}

      {isLoading && (
        <div className="text-sm text-muted-foreground">Loading scenarios…</div>
      )}

      {data && data.mine.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No scenarios yet. Click <strong>New scenario</strong> above to start.
        </div>
      )}

      {data && data.mine.length > 0 && (
        <ul className="space-y-2">
          {data.mine.map((s) => (
            <li
              key={s.id}
              className="rounded-lg border border-border bg-card p-4 flex items-center justify-between gap-3"
            >
              <Link
                to="/$league/scenarios/$scenarioId"
                params={{ league: league.slug, scenarioId: s.id }}
                className="flex-1 min-w-0 hover:opacity-80"
              >
                <div className="font-medium truncate">{s.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                  <span>
                    Updated{" "}
                    {new Date(s.updatedAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  {s.forkedFromCreatorSnapshot && (
                    <span className="rounded-full border border-border px-1.5 py-0.5">
                      Forked from {s.forkedFromCreatorSnapshot}
                    </span>
                  )}
                  {s.hasGoblinCommentary && (
                    <span className="inline-flex items-center gap-0.5 text-primary">
                      <Sparkles className="w-3 h-3" /> Goblin
                    </span>
                  )}
                </div>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteId(s.id)}
                aria-label="Delete scenario"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {data && data.shared.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Shared with you
          </h2>
          <ul className="space-y-2">
            {data.shared.map((s) => (
              <li
                key={s.id}
                className="rounded-lg border border-border bg-card p-4"
              >
                <Link
                  to="/$league/scenarios/$scenarioId"
                  params={{ league: league.slug, scenarioId: s.id }}
                  className="block hover:opacity-80"
                >
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    By {s.creatorName} · read-only
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this scenario?</DialogTitle>
            <DialogDescription>
              This can't be undone. Your real picks aren't affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMut.mutate(deleteId)}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
